import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listWaitlistEntries,
  removeWaitlistEntry,
  type WaitlistEntry,
} from '../api/waitlist';
import { formatDate } from '../lib/format';

interface EntryRowProps {
  entry: WaitlistEntry;
  onRemove: (email: string) => void;
  pendingEmail: string | null;
}

function daysSince(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const days = Math.floor(ms / 86_400_000);
  if (days === 0) return 'today';
  if (days === 1) return '1 day';
  return `${days} days`;
}

function EntryRow({ entry, onRemove, pendingEmail }: EntryRowProps): React.ReactElement {
  const isPending = pendingEmail === entry.email;
  return (
    <tr>
      <td className="px-4 py-3 font-mono text-xs text-text-primary">{entry.email}</td>
      <td className="px-4 py-3 text-text-muted text-xs">{entry.source ?? '—'}</td>
      <td className="px-4 py-3 text-text-muted text-xs">{formatDate(entry.signedUpAt)}</td>
      <td className="px-4 py-3 text-text-muted text-xs">{daysSince(entry.signedUpAt)}</td>
      <td className="px-4 py-3 text-text-muted text-xs max-w-xs truncate" title={entry.userAgent ?? ''}>
        {entry.userAgent ?? '—'}
      </td>
      <td className="px-4 py-3 text-xs">
        <button
          type="button"
          onClick={() => onRemove(entry.email)}
          disabled={isPending}
          className="text-red-600 hover:text-red-700 hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isPending ? 'Removing…' : 'Remove'}
        </button>
      </td>
    </tr>
  );
}

const TH = 'px-4 py-3 text-left text-xs font-semibold text-text-secondary uppercase tracking-wide';

interface WaitlistTableProps {
  items: WaitlistEntry[];
  isLoading: boolean;
  onRemove: (email: string) => void;
  pendingEmail: string | null;
}

function WaitlistTable({ items, isLoading, onRemove, pendingEmail }: WaitlistTableProps): React.ReactElement {
  return (
    <div className="bg-white border border-border rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-subtle border-b border-border">
          <tr>
            <th scope="col" className={TH}>Email</th>
            <th scope="col" className={TH}>Source</th>
            <th scope="col" className={TH}>Signed Up</th>
            <th scope="col" className={TH}>Days Since</th>
            <th scope="col" className={TH}>User Agent</th>
            <th scope="col" className={TH}>Remove</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {items.map((entry) => (
            <EntryRow
              key={entry.email}
              entry={entry}
              onRemove={onRemove}
              pendingEmail={pendingEmail}
            />
          ))}
          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={6} className="px-4 py-8 text-center text-text-muted">
                No waitlist signups yet. When someone joins via the marketing CTA, they appear here.
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

export function Waitlist(): React.ReactElement {
  const [cursor, setCursor] = useState<string | undefined>();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['waitlist', { cursor }],
    queryFn: () => listWaitlistEntries({ cursor, limit: 100 }),
    staleTime: 30_000,
  });

  const removeMut = useMutation({
    mutationFn: (email: string) => removeWaitlistEntry(email),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['waitlist'] });
    },
  });

  const handleRemove = (email: string): void => {
    if (!window.confirm(`Remove ${email} from waitlist?`)) return;
    removeMut.mutate(email);
  };

  const items = data?.items ?? [];
  const pendingEmail = removeMut.isPending ? (removeMut.variables ?? null) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Waitlist</h1>
          <p className="text-sm text-text-muted mt-0.5">
            Marketing-site signups awaiting an invite. Each page sorted newest-first;
            multi-page chronological order requires a future GSI1 migration.
          </p>
        </div>
        <div className="text-sm text-text-muted">
          {items.length > 0 && `${items.length} on this page`}
        </div>
      </div>

      {isLoading && <p className="text-sm text-text-muted">Loading…</p>}

      <WaitlistTable
        items={items}
        isLoading={isLoading}
        onRemove={handleRemove}
        pendingEmail={pendingEmail}
      />

      {data?.nextCursor && (
        <button
          onClick={() => setCursor(data.nextCursor ?? undefined)}
          className="text-sm text-brand-600 hover:underline"
        >
          Next page →
        </button>
      )}
    </div>
  );
}
